import { type ComponentType, lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { AuthLayout } from "./auth/AuthLayout";
import { ProtectedRoute, PublicOnlyRoute } from "./auth/RouteGuards";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { AppShell } from "./shell/AppShell";
import { MovedRoute, PlaygroundRedirect } from "./shell/redirects";

/* Every page is its own chunk: the login page shouldn't ship the agent
 * editor, and the public chat embed shouldn't ship the console at all.
 * Navigations run inside router transitions, so the previous page stays
 * on screen while a chunk loads and the Suspense fallbacks below almost
 * never paint. They exist for the cold-load path, where the boot screen
 * covers them anyway. */
function lazyRoute<K extends string, T extends Record<K, ComponentType>>(
	loader: () => Promise<T>,
	name: K,
) {
	return lazy(async () => ({ default: (await loader())[name] }));
}

const LoginPage = lazyRoute(() => import("./auth/pages/LoginPage"), "LoginPage");
const ForgotPasswordPage = lazyRoute(
	() => import("./auth/pages/ForgotPasswordPage"),
	"ForgotPasswordPage",
);
const ResetPasswordPage = lazyRoute(
	() => import("./auth/pages/ResetPasswordPage"),
	"ResetPasswordPage",
);
const VerifyEmailPage = lazyRoute(() => import("./auth/pages/VerifyEmailPage"), "VerifyEmailPage");

const ChatEmbedPage = lazyRoute(() => import("./pages/chatUi/ChatEmbedPage"), "ChatEmbedPage");
const ChatUiStudioPage = lazyRoute(
	() => import("./pages/chatUi/ChatUiStudioPage"),
	"ChatUiStudioPage",
);

const WorkspaceOnboardingPage = lazyRoute(
	() => import("./pages/onboarding/WorkspaceOnboardingPage"),
	"WorkspaceOnboardingPage",
);

const AgentListPage = lazyRoute(() => import("./pages/profiles/AgentListPage"), "AgentListPage");
const AgentCreatePage = lazyRoute(
	() => import("./pages/profiles/AgentCreatePage"),
	"AgentCreatePage",
);
const AgentDetailPage = lazyRoute(
	() => import("./pages/profiles/AgentDetailPage"),
	"AgentDetailPage",
);
const AgentTriggerDetailPage = lazyRoute(
	() => import("./pages/profiles/AgentTriggerDetailPage"),
	"AgentTriggerDetailPage",
);

const ApprovalListPage = lazyRoute(
	() => import("./pages/approvals/ApprovalListPage"),
	"ApprovalListPage",
);
const ApprovalDetailPage = lazyRoute(
	() => import("./pages/approvals/ApprovalDetailPage"),
	"ApprovalDetailPage",
);

const SandboxListPage = lazyRoute(
	() => import("./pages/sandboxes/SandboxListPage"),
	"SandboxListPage",
);
const SandboxClassFormPage = lazyRoute(
	() => import("./pages/sandboxes/SandboxClassFormPage"),
	"SandboxClassFormPage",
);

const ConversationListPage = lazyRoute(
	() => import("./pages/conversations/ConversationListPage"),
	"ConversationListPage",
);
const ConversationDetailPage = lazyRoute(
	() => import("./pages/conversations/ConversationDetailPage"),
	"ConversationDetailPage",
);

const ApiKeysPage = lazyRoute(() => import("./pages/apiKeys/ApiKeysPage"), "ApiKeysPage");

const UsageOverviewPage = lazyRoute(
	() => import("./pages/usage/UsageOverviewPage"),
	"UsageOverviewPage",
);
const UsageByAgentPage = lazyRoute(
	() => import("./pages/usage/UsageByAgentPage"),
	"UsageByAgentPage",
);
const UsageByApiKeyPage = lazyRoute(
	() => import("./pages/usage/UsageByApiKeyPage"),
	"UsageByApiKeyPage",
);

const WorkspaceSettingsPage = lazyRoute(
	() => import("./pages/settings/WorkspaceSettingsPage"),
	"WorkspaceSettingsPage",
);
const MembersPage = lazyRoute(() => import("./pages/settings/MembersPage"), "MembersPage");
const ProvidersPage = lazyRoute(() => import("./pages/settings/ProvidersPage"), "ProvidersPage");

function NotFoundPage() {
	return (
		<div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-6 text-center">
			<div className="animate-forge-rise space-y-3">
				<p className="label-micro text-primary">Cold slab</p>
				<h2 className="font-display font-wide text-6xl font-extrabold uppercase leading-none tracking-tight">
					404
				</h2>
				<p className="text-sm text-muted-foreground">There is no page at this address.</p>
			</div>
			<a
				href="/agents"
				className="label-micro rounded-sm border border-border px-4 py-2.5 text-foreground/80 transition-colors hover:border-input hover:bg-accent hover:text-foreground"
			>
				Back to agents
			</a>
		</div>
	);
}

export function App() {
	return (
		<BrowserRouter>
			<RouteErrorBoundary>
				<Suspense fallback={<div className="min-h-screen bg-background" />}>
					<Routes>
						<Route element={<PublicOnlyRoute />}>
							<Route element={<AuthLayout />}>
								<Route path="/login" element={<LoginPage />} />
								<Route path="/forgot-password" element={<ForgotPasswordPage />} />
								<Route path="/reset-password" element={<ResetPasswordPage />} />
								<Route path="/verify-email" element={<VerifyEmailPage />} />
							</Route>
						</Route>

						{/* Public, full-bleed chat document: live embeds (?e=token) for
						    anonymous visitors, scripted preview for the studio iframe. */}
						<Route path="/embed/chat" element={<ChatEmbedPage />} />

						<Route element={<ProtectedRoute />}>
							<Route path="/onboarding/workspace" element={<WorkspaceOnboardingPage />} />

							<Route path="/" element={<AppShell />}>
								<Route index element={<Navigate to="agents" replace />} />
								<Route path="agents" element={<AgentListPage />} />
								<Route path="agents/new" element={<AgentCreatePage />} />
								<Route path="agents/:slug" element={<AgentDetailPage />} />
								<Route
									path="agents/:slug/triggers/:triggerId"
									element={<AgentTriggerDetailPage />}
								/>
								<Route path="agents/:slug/:tab" element={<AgentDetailPage />} />
								<Route path="profiles" element={<Navigate to="/agents" replace />} />
								<Route path="profiles/new" element={<Navigate to="/agents/new" replace />} />
								<Route
									path="profiles/:slug"
									element={<MovedRoute to={({ slug = "" }) => `/agents/${slug}`} />}
								/>

								<Route path="approvals" element={<ApprovalListPage />} />
								<Route path="approvals/:id" element={<ApprovalDetailPage />} />

								<Route path="conversations" element={<ConversationListPage />} />
								<Route path="conversations/:id" element={<ConversationDetailPage />} />

								{/* Not in the nav: the studio is reached from an agent. */}
								<Route path="chat-ui" element={<ChatUiStudioPage />} />

								<Route path="usage" element={<UsageOverviewPage />} />
								<Route path="usage/agents/:slug" element={<UsageByAgentPage />} />
								<Route
									path="usage/profiles/:slug"
									element={<MovedRoute to={({ slug = "" }) => `/usage/agents/${slug}`} />}
								/>
								<Route path="usage/api-keys/:id" element={<UsageByApiKeyPage />} />

								<Route path="settings">
									<Route index element={<Navigate to="workspace" replace />} />
									<Route path="workspace" element={<WorkspaceSettingsPage />} />
									<Route path="providers" element={<ProvidersPage />} />
									<Route path="api-keys" element={<ApiKeysPage />} />
									<Route path="members" element={<MembersPage />} />
									<Route path="runtime" element={<SandboxListPage />} />
									<Route path="runtime/new" element={<SandboxClassFormPage />} />
									<Route path="runtime/:id" element={<SandboxClassFormPage />} />
								</Route>

								{/* Moved URLs: old links and bookmarks keep landing. */}
								<Route path="api-keys" element={<MovedRoute to="/settings/api-keys" />} />
								<Route path="sandboxes" element={<MovedRoute to="/settings/runtime" />} />
								<Route path="sandboxes/new" element={<MovedRoute to="/settings/runtime/new" />} />
								<Route
									path="sandboxes/:id"
									element={<MovedRoute to={({ id = "" }) => `/settings/runtime/${id}`} />}
								/>
								<Route path="playground" element={<PlaygroundRedirect />} />
								<Route path="playground/:agentSlug" element={<PlaygroundRedirect />} />
								<Route path="connections/workspace" element={<Navigate to="/agents" replace />} />
								<Route path="connections/users" element={<Navigate to="/agents" replace />} />
							</Route>
						</Route>

						<Route path="*" element={<NotFoundPage />} />
					</Routes>
				</Suspense>
			</RouteErrorBoundary>
		</BrowserRouter>
	);
}
